/**
 * มินิแมพมุมขวาบน
 *
 * วาดด้วย canvas 2D ธรรมดา ไม่ใช่กล้องตัวที่สองใน Babylon
 * เพราะเรนเดอร์ฉาก 3D สองรอบต่อเฟรมแพงเกินไปสำหรับข้อมูลแค่จุดไม่กี่จุด
 *
 * **มินิแมพหมุนตามกล้อง** ไม่ใช่ล็อกทิศเหนือไว้
 * เพราะกล้องของเกมนี้หมุนได้อิสระ (ลากคลิกขวา) ถ้าแผนที่ไม่หมุนตาม
 * ของที่อยู่ "ข้างบนจอ" จะไปโผล่ตรงไหนก็ได้บนมินิแมพ แล้วใช้หาทางไม่ได้เลย
 * สิ่งที่อยู่บนมินิแมพด้านบน = สิ่งที่อยู่ไกลออกไปบนจอเสมอ
 *
 * เป็นงานฝั่งภาพล้วน อ่านตำแหน่งจาก state ที่ server ส่งมาอย่างเดียว
 */

import { ROUTE_HALF, routeNetwork, zoneHalf, type ZoneDef } from "@wickhaven/shared";

/** จุดที่จะวาดลงมินิแมพ */
export interface MapDot {
  x: number;
  z: number;
  kind: "monster" | "boss" | "player" | "party" | "self";
  /** ไฮไลต์เป็นเป้าหมายที่เลือกอยู่ */
  selected?: boolean;
}

const COLOR = {
  monster: "#e06a5a",
  boss: "#ffb03a",
  player: "#68d98c",
  /** เพื่อนในปาร์ตี้ (รอบ 20) */
  party: "#5fb6ff",
  self: "#ffcf5a",
  lantern: "#f0d79a",
  /** ประตูไปโซนอื่น สีเดียวกับวงแสงของประตูในฉาก */
  portal: "#8fd8ff",
  ring: "rgba(255,255,255,.14)",
};

/** แกนของแผนที่ในพิกัดโลก (x, z) */
export interface MapAxes {
  upX: number;
  upZ: number;
  rightX: number;
  rightZ: number;
}

/**
 * แกนของมินิแมพจากมุมกล้อง
 *
 * กล้องอยู่ที่ทิศ (cos α, sin α) ของเป้า จึงมองไปทาง -(cos α, sin α)
 * นั่นคือ "ขึ้นบนจอ" ส่วน "ขวาของจอ" ตั้งฉากกับมันตามระบบมือซ้ายของ Babylon
 * ตรวจค่าได้จาก α = -π/2 ซึ่งเป็นมุมเริ่มต้น: ขึ้นบน = +z และ ขวา = +x
 */
export function mapAxes(camAlpha: number): MapAxes {
  return {
    upX: -Math.cos(camAlpha),
    upZ: -Math.sin(camAlpha),
    rightX: -Math.sin(camAlpha),
    rightZ: Math.cos(camAlpha),
  };
}

/**
 * มุมที่ต้องหมุนลูกศรของตัวเอง (เรเดียน ตามเข็มนาฬิกาแบบ canvas)
 *
 * ลูกศรต้นแบบชี้ขึ้น ต้องแปลงทิศที่ตัวละครหัน (sin ry, cos ry) เข้าแกนของแผนที่ก่อน
 * ไม่งั้นลูกศรจะชี้คนละทางกับตัวละครบนจอทันทีที่หมุนกล้อง
 */
export function arrowAngle(facing: number, axes: MapAxes): number {
  const dirX = Math.sin(facing);
  const dirZ = Math.cos(facing);
  const ax = dirX * axes.rightX + dirZ * axes.rightZ;
  const ay = -(dirX * axes.upX + dirZ * axes.upZ);
  return Math.atan2(ax, -ay);
}

export interface Minimap {
  /**
   * @param dots        จุดทั้งหมดที่จะวาด
   * @param selfX,selfZ ตำแหน่งตัวเอง ใช้เป็นจุดกลาง
   * @param facing      ทิศที่ตัวเองหันอยู่ (เรเดียน 0 = หันไป +z)
   * @param camAlpha    มุมกล้องรอบแกนตั้ง ใช้หมุนแผนที่ให้ตรงกับที่เห็นบนจอ
   */
  draw(dots: MapDot[], selfX: number, selfZ: number, facing: number, camAlpha: number): void;
  /** ซูม: +1 = ใกล้ขึ้น −1 = ไกลขึ้น คืนระยะที่มองเห็น (หน่วยโลกขอบถึงขอบ) */
  zoom(dir: number): number;
}

/** ระยะที่มองเห็นได้ (ขอบถึงขอบ) ให้เลือก — ผู้เล่นซูมด้วยปุ่ม +/− หรือล้อเมาส์บนมินิแมพ */
export const MINIMAP_SPANS = [40, 60, 80, 120, 180] as const;
const SPAN_KEY = "wickhaven.minimapSpan";

export function createMinimap(canvas: HTMLCanvasElement, zone: ZoneDef | undefined): Minimap {
  const ctx = canvas.getContext("2d")!;
  const size = canvas.width;
  const half = size / 2;
  /** มองเห็นกว้างกี่หน่วยโลกจากขอบถึงขอบ (จำไว้ในเครื่อง) */
  let span = 80;
  try { span = Number(localStorage.getItem(SPAN_KEY)) || 80; } catch { /* โหมดส่วนตัว */ }
  if (!(MINIMAP_SPANS as readonly number[]).includes(span)) span = 80;
  // ทางเดินจริงของแมพ (ตัวเดียวกับที่ server หนีบการเดิน) คิดครั้งเดียวต่อโซน
  const routes = zone && !zone.town ? routeNetwork(zone) : [];
  const zh = zone ? zoneHalf(zone) : 46;

  return {
    zoom(dir) {
      const i = (MINIMAP_SPANS as readonly number[]).indexOf(span);
      span = MINIMAP_SPANS[Math.max(0, Math.min(MINIMAP_SPANS.length - 1, i - Math.sign(dir)))]!;
      try { localStorage.setItem(SPAN_KEY, String(span)); } catch { /* โหมดส่วนตัว */ }
      return span;
    },
    draw(dots, selfX, selfZ, facing, camAlpha) {
      ctx.clearRect(0, 0, size, size);
      const scale = size / span;
      // มินิแมพวงกลม (รอบ 20): ตัดทุกอย่างให้อยู่ในวง
      ctx.save();
      ctx.beginPath();
      ctx.arc(half, half, half - 1, 0, Math.PI * 2);
      ctx.clip();
      ctx.fillStyle = "rgba(10,18,14,.85)";
      ctx.fillRect(0, 0, size, size);

      const { upX, upZ, rightX, rightZ } = mapAxes(camAlpha);

      // ---- พื้นแผนที่จริง: ขอบแมพ ทางเดิน ลานมอน (หมุนตามกล้องด้วยเมทริกซ์เดียวกับ toMap) ----
      ctx.save();
      ctx.setTransform(rightX * scale, -upX * scale, rightZ * scale, -upZ * scale,
        half - (selfX * rightX + selfZ * rightZ) * scale, half + (selfX * upX + selfZ * upZ) * scale);
      ctx.fillStyle = zone?.town ? "rgba(110,145,95,.65)" : "rgba(20,40,24,.75)";
      ctx.fillRect(-zh, -zh, zh * 2, zh * 2);
      if (zone?.town) {
        // ---- ผังเมืองในมินิแมพ: ทางเดินสี่ทิศ, ลานเมือง, ทางไปลานฝึกหุ่นฟาง, บ่อน้ำ, บ้านเรือน ----
        ctx.fillStyle = "rgba(224,202,152,.85)";
        // ลานเมืองตรงกลาง
        ctx.beginPath();
        ctx.arc(0, 0, 7.5, 0, Math.PI * 2);
        ctx.fill();

        // ทางเดินสี่ทิศ (เหนือ-ใต้-ออก-ตก)
        ctx.fillRect(-1.7, -42, 3.4, 84);
        ctx.fillRect(-42, -1.7, 84, 3.4);

        // ทางเดินเชื่อมไปลานฝึกซ้อมหุ่นฟาง
        ctx.fillRect(-23.4, -17.4, 2.8, 17.4);
        ctx.fillRect(-34, -17.4, 12, 2.8);

        // ลานฝึกซ้อมหุ่นฟาง
        ctx.fillStyle = "rgba(210,185,135,.9)";
        ctx.beginPath();
        ctx.arc(-32, -16, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "rgba(140,105,65,.8)";
        ctx.lineWidth = 0.8;
        ctx.stroke();

        // บ่อน้ำ
        ctx.fillStyle = "rgba(79,169,216,.9)";
        ctx.beginPath();
        ctx.ellipse(-26, 22, 6, 4.2, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "rgba(224,202,152,.85)";
        ctx.lineWidth = 1;
        ctx.stroke();

        // บล็อกบ้านเรือนในมุมทั้งสี่
        ctx.fillStyle = "rgba(145,115,85,.55)";
        for (const [sx, sz] of [[1, 1], [-1, 1], [1, -1], [-1, -1]] as const) {
          ctx.fillRect(sx * 11 - 2.5, sz * 11 - 2.5, 5, 5);
          ctx.fillRect(sx * 17 - 2.5, sz * 9 - 2.5, 5, 5);
          ctx.fillRect(sx * 9 - 2.5, sz * 17 - 2.5, 5, 5);
        }
      } else if (routes.length) {
        ctx.strokeStyle = "rgba(196,178,128,.75)";
        ctx.lineCap = "round";
        ctx.lineWidth = ROUTE_HALF * 2;
        ctx.beginPath();
        for (const [ax, az, bx, bz] of routes) { ctx.moveTo(ax, az); ctx.lineTo(bx, bz); }
        ctx.stroke();
        ctx.fillStyle = "rgba(196,178,128,.75)";
        for (const sp of zone?.spawns ?? []) {
          ctx.beginPath();
          ctx.arc(sp.x, sp.z, sp.radius + 4, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.strokeStyle = "rgba(255,255,255,.35)";
      ctx.lineWidth = 1.5 / scale;
      ctx.strokeRect(-zh, -zh, zh * 2, zh * 2);
      ctx.restore();

      /** แปลงพิกัดโลกเป็นพิกัดบนมินิแมพ โดยให้ตัวเองอยู่กลางเสมอ */
      const toMap = (x: number, z: number): [number, number] => {
        const dx = x - selfX;
        const dz = z - selfZ;
        return [
          half + (dx * rightX + dz * rightZ) * scale,
          // ลบ เพราะแกน y ของ canvas เพิ่มลงล่าง แต่เราอยากให้ "ขึ้น" คือค่าน้อย
          half - (dx * upX + dz * upZ) * scale,
        ];
      };

      // วงอ้างอิงระยะ ช่วยกะระยะทางคร่าว ๆ
      ctx.strokeStyle = COLOR.ring;
      ctx.lineWidth = 1;
      for (const r of [15, 30].filter((x) => x * 2 < span)) {
        ctx.beginPath();
        ctx.arc(half, half, r * scale, 0, Math.PI * 2);
        ctx.stroke();
      }

      // ตะเกียงกลางเมืองเป็นหมุดอ้างอิงทิศ (เฉพาะโซนที่เป็นเมือง)
      if (zone?.town) {
        const [lx, lz] = toMap(0, 0);
        ctx.fillStyle = COLOR.lantern;
        ctx.beginPath();
        ctx.moveTo(lx, lz - 5);
        ctx.lineTo(lx + 4, lz + 3);
        ctx.lineTo(lx - 4, lz + 3);
        ctx.closePath();
        ctx.fill();
      }

      // ประตูไปโซนอื่น — อยู่นอกกรอบก็ยังโชว์ ติดไว้ที่ขอบตามทิศของประตู
      // จะได้รู้ว่าต้องเดินไปทางไหน ทั้งที่ประตูอยู่ไกลเกินระยะที่มินิแมพเห็น
      for (const p of zone?.portals ?? []) {
        const [px, py] = toMap(p.x, p.z);
        // อยู่นอกวง: ปักที่ขอบวงตามทิศของประตู
        const R = half - 7;
        const dx = px - half, dy = py - half, d = Math.hypot(dx, dy);
        const clipped = d > R;
        const cx = clipped ? half + (dx / d) * R : px;
        const cy = clipped ? half + (dy / d) * R : py;
        ctx.strokeStyle = COLOR.portal;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(cx, cy, clipped ? 3.5 : 5, 0, Math.PI * 2);
        ctx.stroke();
      }

      for (const d of dots) {
        if (d.kind === "self") continue;
        const [x, y] = toMap(d.x, d.z);
        if (x < -4 || y < -4 || x > size + 4 || y > size + 4) continue;

        const r = d.kind === "boss" ? 4.5 : d.kind === "player" || d.kind === "party" ? 3.5 : 2.8;
        ctx.fillStyle = COLOR[d.kind];
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();

        if (d.selected) {
          ctx.strokeStyle = COLOR.self;
          ctx.lineWidth = 1.6;
          ctx.beginPath();
          ctx.arc(x, y, r + 3, 0, Math.PI * 2);
          ctx.stroke();
        }
      }

      ctx.restore();
      // ขอบวง + ป้ายทิศเหนือ (โลก +z) ที่ขอบ หมุนตามกล้อง — มินิแมพหมุนตามจอ ป้ายนี้บอกว่าเหนืออยู่ไหน
      ctx.strokeStyle = "rgba(255,207,90,.55)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(half, half, half - 1.5, 0, Math.PI * 2);
      ctx.stroke();
      {
        const nx = upZ, ny = rightZ; // องค์ประกอบของ +z บนแกนขึ้น/ขวาของจอ
        const len = Math.hypot(nx, ny) || 1;
        const ex = half + (ny / len) * (half - 8), ey = half - (nx / len) * (half - 8);
        ctx.fillStyle = "rgba(10,16,28,.9)";
        ctx.beginPath();
        ctx.arc(ex, ey, 6.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#ffcf5a";
        ctx.font = "bold 9px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("N", ex, ey + 0.5);
      }

      // ตัวเองเป็นลูกศรชี้ทิศที่หันอยู่ อยู่กลางจอเสมอ
      ctx.save();
      ctx.translate(half, half);
      ctx.rotate(arrowAngle(facing, mapAxes(camAlpha)));
      ctx.fillStyle = COLOR.self;
      ctx.beginPath();
      ctx.moveTo(0, -6);
      ctx.lineTo(4.5, 5);
      ctx.lineTo(0, 2.5);
      ctx.lineTo(-4.5, 5);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    },
  };
}
